import { useCallback, useEffect, useState } from 'react';
import type { HomeRow } from '../api/client.js';
import { EMPTY_VALUE } from '../format.js';
import { COLUMN_MIN_WIDTH, FROZEN_NAME_DEFAULT_WIDTH } from './entity-table.js';
import { cohortLabel, homeValueLabel, humanizeToken } from './homes-filters.js';

// Column model for the Homes screener (one row per care home). Same contract
// as Triage's screener-columns + use-screener-column-config: a frozen Name pane
// plus ordered / sized / visible right-pane columns, persisted per panel and
// driven by the shared ColumnsMenu / ColumnHeaderCell. Each column id IS the
// /homes response field, so it doubles as the server sort key and the
// `columns=` list (only visible columns are requested).

export const HOMES_COLUMN_IDS = [
  'country_code',
  'lau_name',
  'urbanicity',
  'operator_sector',
  'setting',
  'care_level',
  'cohorts',
  'beds',
  'tenure',
  'rating_grade',
] as const;
export type HomesColumnId = (typeof HOMES_COLUMN_IDS)[number];

interface HomesColumnDef {
  label: string;
  defaultWidth: number;
  numeric: boolean;
  /** Shown on a fresh layout; the rest are one tick away in Columns. */
  defaultVisible: boolean;
  /** Extra response fields the cell needs (e.g. the basis or unit). */
  extra?: readonly string[];
  tooltip?: string;
}

// The agreed screener set (Alex 2026-10-16): Home (frozen) · Country · Municipality · Urban / rural · Ownership type ·
// Setting · Care level · Specialisation · Beds · Freehold tenure · Rating. Everything else about a home lives in Home
// record. City was replaced the same day ("City does not address rural locations"): Municipality is the official
// unit every home sits in (LAU; GB = local authority district) and Urban / rural is Eurostat's degree of urbanisation
// at the home's own point.
export const HOMES_COLUMN_DEFS: Record<HomesColumnId, HomesColumnDef> = {
  country_code: { label: 'Country', defaultWidth: 64, numeric: false, defaultVisible: true },
  lau_name: {
    label: 'Municipality',
    defaultWidth: 130,
    numeric: false,
    defaultVisible: true,
    tooltip: 'The municipality the home sits in (EU LAU; GB: local authority district).',
  },
  urbanicity: {
    label: 'Urban / rural',
    defaultWidth: 104,
    numeric: false,
    defaultVisible: true,
    tooltip:
      'Eurostat degree of urbanisation at the home’s location: City, Town & suburbs or Rural (JRC GHSL grid).',
  },
  operator_sector: {
    label: 'Ownership type',
    defaultWidth: 104,
    numeric: false,
    defaultVisible: true,
    tooltip: 'Who runs the home: private, non-profit or public.',
  },
  setting: { label: 'Setting', defaultWidth: 116, numeric: false, defaultVisible: true },
  care_level: { label: 'Care level', defaultWidth: 104, numeric: false, defaultVisible: true },
  cohorts: {
    label: 'Specialisation',
    defaultWidth: 190,
    numeric: false,
    defaultVisible: true,
    tooltip: 'Who the home cares for (dementia, learning disability, mental health …).',
  },
  beds: {
    label: 'Beds',
    defaultWidth: 64,
    numeric: true,
    defaultVisible: true,
    extra: ['beds_basis'],
    tooltip: 'Published capacity only (registered / licensed / operational) — never an estimate.',
  },
  tenure: {
    label: 'Freehold tenure',
    defaultWidth: 140,
    numeric: false,
    defaultVisible: true,
    tooltip: 'Who holds the freehold: the operator, or a landlord it leases from.',
  },
  rating_grade: {
    label: 'Rating',
    defaultWidth: 150,
    numeric: false,
    defaultVisible: true,
    extra: ['rating', 'rating_scale'],
    tooltip:
      'Three grades read off each regulator’s own scale or findings. Hover a cell for the native rating; Home record shows it in full.',
  },
};

/** Fields every request carries regardless of visible columns. */
const ALWAYS = ['home_key', 'name', 'lifecycle', 'operator_entity_id'] as const;

/** The `columns=` list for a column layout. */
export function homesRequestColumns(columns: readonly HomesColumnState[]): string {
  const set = new Set<string>(ALWAYS);
  for (const c of columns) {
    if (!c.visible) continue;
    set.add(c.id);
    for (const e of HOMES_COLUMN_DEFS[c.id].extra ?? []) set.add(e);
  }
  return Array.from(set).join(',');
}

// --- cell rendering -----------------------------------------------------

const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

export interface HomeCell {
  display: string;
  title?: string;
}

export function renderHomeCell(row: HomeRow, id: HomesColumnId): HomeCell {
  const v = row[id];
  switch (id) {
    case 'country_code':
      return {
        display: str(v) ?? EMPTY_VALUE,
        title: str(v) ? homeValueLabel('country', str(v)) : undefined,
      };
    case 'urbanicity':
      return { display: str(v) ? homeValueLabel('urbanicity', str(v)) : EMPTY_VALUE };
    case 'setting':
      return { display: str(v) ? homeValueLabel('setting', str(v)) : EMPTY_VALUE };
    case 'care_level':
      return { display: str(v) ? homeValueLabel('careLevel', str(v)) : EMPTY_VALUE };
    case 'operator_sector':
      return {
        display: str(v) && v !== 'unknown' ? homeValueLabel('operatorSector', str(v)) : EMPTY_VALUE,
      };
    case 'tenure':
      return {
        display: str(v) && v !== 'unknown' ? homeValueLabel('tenure', str(v)) : EMPTY_VALUE,
      };
    case 'cohorts': {
      const arr = Array.isArray(v) ? (v as string[]) : [];
      if (!arr.length) return { display: EMPTY_VALUE };
      const text = arr.map(cohortLabel).join(', ');
      return { display: text, title: text };
    }
    case 'beds': {
      const n = num(v);
      const basis = str(row['beds_basis']);
      return {
        display: n === null ? EMPTY_VALUE : Math.round(n).toLocaleString('en-GB'),
        title: basis ? `Basis: ${basis}` : undefined,
      };
    }
    case 'rating_grade': {
      const native = str(row['rating']);
      const scale = str(row['rating_scale']);
      const nativeTitle = native
        ? `${humanizeToken(native)}${scale ? ` — ${scale}` : ''}`
        : undefined;
      if (str(v)) return { display: homeValueLabel('ratingGrade', str(v)), title: nativeTitle };
      // A rating exists but the regulator publishes no quality judgement (e.g. a licence register).
      return native ? { display: 'Not graded', title: nativeTitle } : { display: EMPTY_VALUE };
    }
    default: {
      const s = str(v);
      return { display: s ?? EMPTY_VALUE, title: s ?? undefined };
    }
  }
}

// --- persisted layout ---------------------------------------------------

const SCHEMA_VERSION = 1;

export interface HomesColumnState {
  id: HomesColumnId;
  width: number;
  visible: boolean;
}

export interface HomesColumnConfig {
  nameWidth: number;
  columns: HomesColumnState[];
}

export interface HomesColumnActions {
  setNameWidth: (px: number) => void;
  setColumnWidth: (id: HomesColumnId, px: number) => void;
  setColumnVisible: (id: HomesColumnId, visible: boolean) => void;
  moveColumn: (id: HomesColumnId, targetIndex: number) => void;
  resetToDefaults: () => void;
}

export function defaultHomesColumnState(): HomesColumnState[] {
  return HOMES_COLUMN_IDS.map((id) => ({
    id,
    width: HOMES_COLUMN_DEFS[id].defaultWidth,
    visible: HOMES_COLUMN_DEFS[id].defaultVisible,
  }));
}

function defaults(): HomesColumnConfig {
  return { nameWidth: FROZEN_NAME_DEFAULT_WIDTH + 40, columns: defaultHomesColumnState() };
}

export function loadHomesColumnConfig(raw: string | null): HomesColumnConfig {
  if (!raw) return defaults();
  try {
    const parsed = JSON.parse(raw) as {
      v?: number;
      nameWidth?: unknown;
      columns?: unknown;
    };
    if (parsed.v !== SCHEMA_VERSION || typeof parsed.nameWidth !== 'number') return defaults();
    if (!Array.isArray(parsed.columns)) return defaults();
    const known = new Set<string>(HOMES_COLUMN_IDS);
    const seen = new Set<string>();
    const stored: HomesColumnState[] = [];
    // City was retired 2026-10-16 for Municipality + Urban / rural: they take its place in a saved layout.
    for (const c of parsed.columns as Array<{ id?: unknown; width?: unknown; visible?: unknown }>) {
      if (c && c.id === 'city') {
        for (const id of ['lau_name', 'urbanicity'] as const) {
          if (seen.has(id)) continue;
          seen.add(id);
          stored.push({
            id,
            width: HOMES_COLUMN_DEFS[id].defaultWidth,
            visible: c.visible !== false,
          });
        }
        continue;
      }
      if (
        !c ||
        typeof c.id !== 'string' ||
        !known.has(c.id) ||
        seen.has(c.id) ||
        typeof c.width !== 'number' ||
        typeof c.visible !== 'boolean'
      )
        continue;
      seen.add(c.id);
      stored.push({
        id: c.id as HomesColumnId,
        width: Math.max(COLUMN_MIN_WIDTH, c.width),
        visible: c.visible,
      });
    }
    // Columns shipped after the layout was saved join at the end, at their default visibility.
    for (const id of HOMES_COLUMN_IDS) {
      if (!seen.has(id))
        stored.push({
          id,
          width: HOMES_COLUMN_DEFS[id].defaultWidth,
          visible: HOMES_COLUMN_DEFS[id].defaultVisible,
        });
    }
    return { nameWidth: Math.max(COLUMN_MIN_WIDTH, parsed.nameWidth), columns: stored };
  } catch {
    return defaults();
  }
}

export function useHomesColumnConfig(storageKey: string): [HomesColumnConfig, HomesColumnActions] {
  const [config, setConfig] = useState<HomesColumnConfig>(() => {
    try {
      return loadHomesColumnConfig(localStorage.getItem(storageKey));
    } catch {
      return defaults();
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify({ v: SCHEMA_VERSION, ...config }));
    } catch {
      // Non-critical (private mode etc).
    }
  }, [config, storageKey]);

  const setNameWidth = useCallback((px: number) => {
    setConfig((c) => ({ ...c, nameWidth: Math.max(COLUMN_MIN_WIDTH, Math.round(px)) }));
  }, []);
  const setColumnWidth = useCallback((id: HomesColumnId, px: number) => {
    const width = Math.max(COLUMN_MIN_WIDTH, Math.round(px));
    setConfig((c) => ({
      ...c,
      columns: c.columns.map((col) => (col.id === id ? { ...col, width } : col)),
    }));
  }, []);
  const setColumnVisible = useCallback((id: HomesColumnId, visible: boolean) => {
    setConfig((c) => ({
      ...c,
      columns: c.columns.map((col) => (col.id === id ? { ...col, visible } : col)),
    }));
  }, []);
  const moveColumn = useCallback((id: HomesColumnId, targetIndex: number) => {
    setConfig((c) => {
      const from = c.columns.findIndex((col) => col.id === id);
      if (from === -1) return c;
      const to = Math.max(0, Math.min(c.columns.length - 1, targetIndex));
      if (from === to) return c;
      const next = [...c.columns];
      const [moved] = next.splice(from, 1);
      if (!moved) return c;
      next.splice(to, 0, moved);
      return { ...c, columns: next };
    });
  }, []);
  const resetToDefaults = useCallback(() => setConfig(defaults()), []);

  return [config, { setNameWidth, setColumnWidth, setColumnVisible, moveColumn, resetToDefaults }];
}
